/**
 * Owner: Masa Bayu
 * File: qx-writer-page-sections.ts
 * Date:Time: 2026-10-16 07:52
 * Description: Word-like page sections · label · align · italic.
 */

export const SECTION_ID_ATTR = 'data-qxw-section-id';
export const SECTION_BREAK_ATTR = 'data-qxw-section-break';
export const SECTIONS_JSON_ATTR = 'data-qxw-sections';

export const SECTION_FORMATS = [
  'arabic',
  'roman-lower',
  'roman-upper',
  'none',
] as const;

export type SectionPageFormat = (typeof SECTION_FORMATS)[number];

export const SECTION_LABELS = ['number', 'page'] as const;
export type SectionPageLabel = (typeof SECTION_LABELS)[number];

export const SECTION_ALIGNS = ['center', 'right'] as const;
export type SectionPageAlign = (typeof SECTION_ALIGNS)[number];

export type PageSectionProps = {
  readonly id: number;
  readonly format: SectionPageFormat;
  readonly startAt: number;
  readonly linkToPrevious: boolean;
  readonly label: SectionPageLabel;
  readonly align: SectionPageAlign;
  readonly italic: boolean;
};

export type SectionPagePaint = {
  readonly text: string;
  readonly visible: boolean;
  readonly value: number;
  readonly sectionId: number;
  readonly align: SectionPageAlign;
  readonly italic: boolean;
};

const SPINE_PAGE = 'qxw-spine-page';

const ROMAN_MAP: readonly [number, string][] = [
  [1000, 'm'],
  [900, 'cm'],
  [500, 'd'],
  [400, 'cd'],
  [100, 'c'],
  [90, 'xc'],
  [50, 'l'],
  [40, 'xl'],
  [10, 'x'],
  [9, 'ix'],
  [5, 'v'],
  [4, 'iv'],
  [1, 'i'],
];

export function parseSectionPageFormat(value: unknown): SectionPageFormat {
  if (typeof value !== 'string') return 'arabic';
  const t = value.trim().toLowerCase();
  return (SECTION_FORMATS as readonly string[]).includes(t)
    ? (t as SectionPageFormat)
    : 'arabic';
}

export function parseSectionPageLabel(value: unknown): SectionPageLabel {
  return value === 'page' ? 'page' : 'number';
}

export function parseSectionPageAlign(value: unknown): SectionPageAlign {
  return value === 'right' ? 'right' : 'center';
}

function toRoman(n: number, upper: boolean): string {
  if (!Number.isFinite(n) || n < 1) return upper ? 'I' : 'i';
  let rest = Math.min(3999, Math.floor(n));
  let out = '';
  for (const [value, glyph] of ROMAN_MAP) {
    while (rest >= value) {
      out += glyph;
      rest -= value;
    }
  }
  return upper ? out.toUpperCase() : out;
}

export function formatSectionPageValue(
  format: SectionPageFormat,
  n: number,
  label: SectionPageLabel = 'number',
): {
  readonly text: string;
  readonly visible: boolean;
} {
  if (format === 'none') return { text: '', visible: false };
  let numeral = String(n);
  if (format === 'roman-upper') numeral = toRoman(n, true);
  else if (format === 'roman-lower') numeral = toRoman(n, false);
  const text = label === 'page' ? `Page ${numeral}` : numeral;
  return { text, visible: true };
}

export function listSectionSpineSheets(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll(`.${SPINE_PAGE}`)).filter(
    (el): el is HTMLElement => el instanceof HTMLElement,
  );
}

export function defaultPageSection(
  id: number,
  format: SectionPageFormat = 'arabic',
): PageSectionProps {
  return {
    id,
    format,
    startAt: 1,
    linkToPrevious: false,
    label: 'number',
    align: 'center',
    italic: false,
  };
}

const defaultSection = defaultPageSection;

function normalizeSection(raw: unknown, fallbackId: number): PageSectionProps {
  if (!raw || typeof raw !== 'object') return defaultSection(fallbackId);
  const rec = raw as Record<string, unknown>;
  const id = typeof rec.id === 'number' && Number.isFinite(rec.id)
    ? Math.max(0, Math.floor(rec.id))
    : fallbackId;
  const startRaw = typeof rec.startAt === 'number' ? rec.startAt : 1;
  const startAt = Number.isFinite(startRaw) ? Math.max(1, Math.floor(startRaw)) : 1;
  return {
    id,
    format: parseSectionPageFormat(rec.format),
    startAt,
    linkToPrevious: id === 0 ? false : rec.linkToPrevious === true,
    label: parseSectionPageLabel(rec.label),
    align: parseSectionPageAlign(rec.align),
    italic: rec.italic === true,
  };
}

export function readPageSections(root: HTMLElement): PageSectionProps[] {
  const raw = root.getAttribute(SECTIONS_JSON_ATTR);
  if (!raw) return [defaultSection(0)];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return [defaultSection(0)];
    return parsed.map((item, index) => normalizeSection(item, index));
  } catch {
    return [defaultSection(0)];
  }
}

export function writePageSections(root: HTMLElement, sections: readonly PageSectionProps[]): void {
  const cleaned = sections.map((section, index) => ({
    id: index,
    format: parseSectionPageFormat(section.format),
    startAt: Math.max(1, Math.floor(section.startAt || 1)),
    linkToPrevious: index === 0 ? false : Boolean(section.linkToPrevious),
    label: parseSectionPageLabel(section.label),
    align: parseSectionPageAlign(section.align),
    italic: section.italic === true,
  }));
  root.setAttribute(SECTIONS_JSON_ATTR, JSON.stringify(cleaned));
}

export function rootUsesPageSections(root: HTMLElement): boolean {
  return Boolean(root.getAttribute(SECTIONS_JSON_ATTR));
}

export function ensurePageSections(root: HTMLElement): PageSectionProps[] {
  const sheets = listSectionSpineSheets(root);
  let sections = readPageSections(root);
  if (sheets.length === 0) {
    writePageSections(root, sections);
    return sections;
  }
  const hasIds = sheets.some((sheet) => sheet.hasAttribute(SECTION_ID_ATTR));
  if (!hasIds) {
    for (const sheet of sheets) sheet.setAttribute(SECTION_ID_ATTR, '0');
  }
  if (!root.getAttribute(SECTIONS_JSON_ATTR)) {
    sections = [defaultSection(0, 'arabic')];
    writePageSections(root, sections);
  }
  const maxId = sheets.reduce((max, sheet) => {
    const id = Number(sheet.getAttribute(SECTION_ID_ATTR));
    return Number.isFinite(id) ? Math.max(max, id) : max;
  }, 0);
  sections = readPageSections(root);
  while (sections.length <= maxId) {
    sections = [...sections, defaultSection(sections.length)];
  }
  writePageSections(root, sections);
  return readPageSections(root);
}

export function sectionSheetMatter(sheet: HTMLElement): 'cover' | 'front' | 'main' | 'back' {
  const raw = sheet.getAttribute('data-qxw-spine-matter');
  if (raw === 'cover' || raw === 'front' || raw === 'main' || raw === 'back') return raw;
  const kind = sheet.getAttribute('data-qxw-spine-kind') || '';
  if (kind === 'cover') return 'cover';
  if (
    kind === 'title-page'
    || kind === 'copyright-page'
    || kind === 'table-of-contents'
    || kind === 'chapter-opening'
  ) {
    return 'front';
  }
  if (kind === 'back-matter' || kind === 'author-bio') return 'back';
  return 'main';
}

/**
 * Academic seed · Section 0 Front (roman, Start at 1) · Section 1 Body
 * (arabic, Start at 1, not linked) · cover unmarked.
 */
export function seedAcademicPageSections(root: HTMLElement): PageSectionProps[] {
  const sheets = listSectionSpineSheets(root);
  const sections: PageSectionProps[] = [
    {
      id: 0, format: 'roman-lower', startAt: 1, linkToPrevious: false,
      label: 'number', align: 'center', italic: false,
    },
    {
      id: 1, format: 'arabic', startAt: 1, linkToPrevious: false,
      label: 'number', align: 'center', italic: false,
    },
  ];
  const firstBody = sheets.find((el) => {
    const m = sectionSheetMatter(el);
    return m === 'main' || m === 'back';
  });
  for (const sheet of sheets) {
    const matter = sectionSheetMatter(sheet);
    const sectionId = matter === 'main' || matter === 'back' ? 1 : 0;
    sheet.setAttribute(SECTION_ID_ATTR, String(sectionId));
    if (sheet === firstBody) sheet.setAttribute(SECTION_BREAK_ATTR, 'next-page');
    else sheet.removeAttribute(SECTION_BREAK_ATTR);
  }
  writePageSections(root, sections);
  return sections;
}

/**
 * Insert Section Break (Next Page) · new section starts on the next sheet
 * (Word: current page stays in the old section).
 */
export function insertSectionBreakNextPage(
  root: HTMLElement,
  focusSheet?: HTMLElement | null,
): { readonly ok: boolean; readonly sectionId: number } {
  const sheets = listSectionSpineSheets(root);
  if (sheets.length === 0) return { ok: false, sectionId: 0 };
  ensurePageSections(root);
  const focus = focusSheet && sheets.includes(focusSheet) ? focusSheet : sheets[0]!;
  const focusIndex = sheets.indexOf(focus);
  const breakAt = focusIndex < sheets.length - 1 ? focusIndex + 1 : focusIndex;
  const sections = readPageSections(root);
  const newId = sections.length;
  writePageSections(root, [
    ...sections,
    {
      id: newId, format: 'arabic', startAt: 1, linkToPrevious: false,
      label: 'number', align: 'center', italic: false,
    },
  ]);
  for (let i = breakAt; i < sheets.length; i += 1) {
    const sheet = sheets[i]!;
    sheet.setAttribute(SECTION_ID_ATTR, String(newId));
    if (i === breakAt) sheet.setAttribute(SECTION_BREAK_ATTR, 'next-page');
    else sheet.removeAttribute(SECTION_BREAK_ATTR);
  }
  return { ok: true, sectionId: newId };
}

export function sheetSectionId(sheet: HTMLElement): number {
  const id = Number(sheet.getAttribute(SECTION_ID_ATTR));
  return Number.isFinite(id) && id >= 0 ? Math.floor(id) : 0;
}

export function updatePageSectionProps(
  root: HTMLElement,
  sectionId: number,
  patch: Partial<
    Pick<
      PageSectionProps,
      'format' | 'startAt' | 'linkToPrevious' | 'label' | 'align' | 'italic'
    >
  >,
): PageSectionProps[] {
  const sections = ensurePageSections(root).map((section) => {
    if (section.id !== sectionId) return section;
    return {
      ...section,
      format: patch.format !== undefined ? parseSectionPageFormat(patch.format) : section.format,
      startAt:
        patch.startAt !== undefined
          ? Math.max(1, Math.floor(patch.startAt))
          : section.startAt,
      linkToPrevious:
        section.id === 0
          ? false
          : patch.linkToPrevious !== undefined
            ? Boolean(patch.linkToPrevious)
            : section.linkToPrevious,
      label: patch.label !== undefined ? parseSectionPageLabel(patch.label) : section.label,
      align: patch.align !== undefined ? parseSectionPageAlign(patch.align) : section.align,
      italic: patch.italic !== undefined ? Boolean(patch.italic) : section.italic,
    };
  });
  writePageSections(root, sections);
  return sections;
}

export function currentPageSection(
  root: HTMLElement,
  focusSheet?: HTMLElement | null,
): PageSectionProps {
  const sections = ensurePageSections(root);
  const sheets = listSectionSpineSheets(root);
  const focus = focusSheet && sheets.includes(focusSheet)
    ? focusSheet
    : sheets[0] ?? null;
  if (!focus) return sections[0] ?? defaultSection(0);
  const id = sheetSectionId(focus);
  return sections.find((section) => section.id === id) ?? sections[0] ?? defaultSection(0);
}

export {
  applySectionPageRange,
  computeSectionPagePaints,
  sectionPageSpan,
  type SectionPageSpan,
} from './qx-writer-page-section-paint';
